import React from 'react';

import { ZoomInIcon, ZoomOutIcon } from '@vlabs/icons';
import { Control, Slider } from '@vlabs/uikit';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import st from './DetectionOverlaySettings.module.sass';
import { buildTestid } from '../../../lib/utils';
import { ColorPicker } from '../../color-picker';
import type { DetectionColor } from '../../color-picker';
import { useDetectionOverlayTranslation } from '../i18n';

interface DetectionOverlaySettingsValue {
  withDetections: boolean;
  withColor?: boolean;
  color?: DetectionColor;
  withSize?: boolean;
  dotSize?: number;
}

interface DetectionOverlaySettingsProps extends WithDataTestid, WithClassNameProp {
  settings: DetectionOverlaySettingsValue;
  onChangeSettings: (nextSettings: DetectionOverlaySettingsValue) => void;
  label: string;
}

const displayName = 'DetectionOverlaySettings';
const testid = buildTestid(displayName);

function DetectionOverlaySettings({
  settings,
  onChangeSettings,
  label,
  className = undefined,
  'data-testid': dataTestId,
}: DetectionOverlaySettingsProps): React.JSX.Element {
  const { t } = useDetectionOverlayTranslation();
  const handleToggleDetections = (value: boolean) => {
    onChangeSettings({ ...settings, withDetections: value });
  };
  const handleDotSizeChange = (value: number | number[]) => {
    onChangeSettings({
      ...settings,
      dotSize: Array.isArray(value) ? value[0] : value,
    });
  };
  const handleColorChange = (color: DetectionColor) => {
    onChangeSettings({ ...settings, color });
  };

  return (
    <div
      className={cn(st.DetectionOverlaySettings, className)}
      {...testid(dataTestId)}
    >
      <Control.Switch
        className={st.DetectionsSwitcher}
        id={`DetectionOverlaySettings${label}`}
        label={label}
        name={label}
        onChange={handleToggleDetections}
        value={settings.withDetections}
      />

      {!!settings.withSize && (
        <React.Fragment>
          <div className={st.Separator} />
          <div className={st.SliderRoot}>
            <span className={st.Label}>{t('size')}</span>

            <div className={st.SliderWrapper}>
              <ZoomOutIcon className={st.Icon} />

              <Slider
                className={st.Slider}
                defaultValue={settings.dotSize}
                disabled={!settings.withDetections}
                max={32}
                min={4}
                onChange={handleDotSizeChange}
              />

              <ZoomInIcon className={st.Icon} />
            </div>
          </div>
        </React.Fragment>
      )}

      {!!settings.withColor && (
        <React.Fragment>
          <div className={st.Separator} />

          <ColorPicker
            disabled={!settings.withDetections}
            onChange={handleColorChange}
            value={settings.color ?? 'positive'}
          />
        </React.Fragment>
      )}
    </div>
  );
}

DetectionOverlaySettings.displayName = displayName;

export { DetectionOverlaySettings };
export type { DetectionOverlaySettingsValue };
